import {Canvas} from '@postermywall/fabricjs-2';
import type {Page} from './page.class';
import type {SnapGuide} from '@PosterWhiteboard/page/alignment-guides/alignment-guides.types';
import {gridToGuides} from '@PosterWhiteboard/page/alignment-guides/snap-geometry.library';

const GRID_STROKE = 'rgba(192,192,192, 0.5)';
const THIN_GRID_LINE_WIDTH = 1;
const THICK_GRID_LINE_WIDTH = 2;
const MAJOR_GRID_INTERVAL = 5;

/** Draws the layout grid over the canvas. Snapping to it is the alignment guides' job; see {@link getSnapGuides}. */
export class RenderGrid {
  public page: Page;
  private canvasCleared = false;

  constructor(page: Page) {
    this.page = page;
    if (this.page.fabricCanvas instanceof Canvas) {
      this.page.fabricCanvas.on('before:render', this.beforeRender.bind(this));
      this.page.fabricCanvas.on('after:render', this.afterRender.bind(this));
    }
  }

  /** The major grid lines as snap guides while the grid is on; items snap to every {@link MAJOR_GRID_INTERVAL}th line, as they always have. */
  public getSnapGuides(): SnapGuide[] {
    if (!this.page.poster.grid.enabled) {
      return [];
    }
    const bounds = {left: 0, top: 0, width: this.page.poster.width, height: this.page.poster.height};
    return gridToGuides(bounds, this.page.poster.grid.size * MAJOR_GRID_INTERVAL);
  }

  private beforeRender(): void {
    if (this.page.fabricCanvas instanceof Canvas && this.page.fabricCanvas.contextTop) {
      this.page.fabricCanvas.contextTopDirty = true;
      this.canvasCleared = true;
    }
  }

  private afterRender(): void {
    this.drawGridLines();
  }

  private drawGridLines(): void {
    if (this.page.poster.grid.enabled && this.canvasCleared) {
      const ctx = this.page.fabricCanvas.contextContainer;
      const offsetX = this.page.fabricCanvas.viewportTransform[4];
      const offsetY = this.page.fabricCanvas.viewportTransform[5];
      const zoom = this.page.fabricCanvas.getZoom();
      const width = this.page.poster.width * zoom;
      const height = this.page.poster.height * zoom;
      const gridSpacing = this.page.poster.grid.size * zoom;
      if (!Number.isFinite(gridSpacing) || gridSpacing <= 0) {
        this.canvasCleared = false;
        return;
      }
      const gridLenX = width / gridSpacing;
      const gridLenY = height / gridSpacing;

      for (let i = 0; i < gridLenY; i++) {
        const y = i * gridSpacing + offsetY;
        this.strokeGridLine(ctx, offsetX, y, width + offsetX, y, i % MAJOR_GRID_INTERVAL === 0);
      }
      for (let i = 0; i < gridLenX; i++) {
        const x = i * gridSpacing + offsetX;
        this.strokeGridLine(ctx, x, offsetY, x, height + offsetY, i % MAJOR_GRID_INTERVAL === 0);
      }
      this.canvasCleared = false;
    }
  }

  private strokeGridLine(ctx: CanvasRenderingContext2D, x1: number, y1: number, x2: number, y2: number, isMajor: boolean): void {
    ctx.save();
    ctx.lineWidth = isMajor ? THICK_GRID_LINE_WIDTH : THIN_GRID_LINE_WIDTH;
    ctx.strokeStyle = GRID_STROKE;
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();
    ctx.restore();
  }
}
